import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Settings,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/contexts/AuthContext'
import { ApiError, api } from '@/services/api'
import { cn, isQolaigEmail } from '@/lib/utils'

export const Route = createFileRoute('/standup')({
  component: StandupPage,
})

interface StandupEntryDto {
  content: string | null
  updated_at: string | null
}

interface StandupBoardCard {
  user: { id: string; nom: string; email: string }
  entry: StandupEntryDto | null
  has_completed_update: boolean
}

interface StandupBoardResponse {
  message: string
  standup_date: string
  cards: StandupBoardCard[]
}

interface UserRow {
  id: string
  nom: string
  email: string
  role: string
  phone?: string | null
}

type MemberEditorState = null | { type: 'create' } | { type: 'edit'; userId: string }

interface MemberDraft {
  nom: string
  email: string
  phone: string
}

interface UserListResponse {
  data: UserRow[]
  count: number
}

const URL_SPLIT = /(https?:\/\/[^\s]+)/g

/** Bleu charte Qolaig */
const CHARTE = '#1CB5C8'

function linkifyLine(line: string): ReactNode[] {
  const parts = line.split(URL_SPLIT)
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline underline-offset-2 break-all hover:opacity-90"
          style={{ color: CHARTE }}
        >
          {part}
        </a>
      )
    }
    return <Fragment key={i}>{part}</Fragment>
  })
}

function StandupTextWithLinks({ text, compact }: { text: string; compact?: boolean }) {
  const lines = text.split('\n')
  return (
    <div
      className={cn(
        'text-sm text-gray-800 break-words',
        compact ? 'leading-snug' : 'leading-relaxed'
      )}
    >
      {lines.map((line, idx) => (
        <span key={idx}>
          {idx > 0 ? <br /> : null}
          {linkifyLine(line)}
        </span>
      ))}
    </div>
  )
}

function toLocalISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

function formatFrenchDate(d: Date): string {
  const raw = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

/** « lundi 11 mai » — sous-titre saisie des temps (Figma) */
function formatStandupTimeInstruction(d: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d)
}

const STANDUP_TIME_STORAGE_PREFIX = 'qolaig-standup-time-entries-v1'
/** Journée ouvrée : 9 h = 1 j (conversion jours réel & quota saisie) */
const HEURES_PAR_JOUR_TRAVAIL = 9
const MAX_STANDUP_DAY_HEURES = HEURES_PAR_JOUR_TRAVAIL

interface StandupTimeRow {
  id: string
  /** Absent sur anciennes données localStorage (même navigateur = vous) */
  userId?: string
  userNom?: string
  projectId: number
  projectLabel: string
  /** Durée en heures (1–8, ou 4 = demi-journée, 8 = journée complète) */
  durationHeures: number
}

interface StandupTimeEntriesApiResponse {
  message: string
  work_date: string
  entries: Array<{
    id: number
    user_id: string
    user_nom: string
    project_id: number
    project_nom: string
    duration_hours: number
  }>
}

function isOwnStandupTimeRow(row: StandupTimeRow, currentUserId: string | undefined): boolean {
  if (!currentUserId) return !row.userId
  return !row.userId || row.userId === currentUserId
}

function rowsFromApiEntries(
  entries: StandupTimeEntriesApiResponse['entries'] | undefined
): StandupTimeRow[] {
  if (!entries?.length) return []
  return entries.map((e) => ({
    id: String(e.id),
    userId: e.user_id,
    userNom: e.user_nom,
    projectId: e.project_id,
    projectLabel: e.project_nom,
    durationHeures: e.duration_hours,
  }))
}

function rowsToApiPayload(rows: StandupTimeRow[]) {
  return rows.map((r) => ({
    project_id: r.projectId,
    duration_hours: r.durationHeures,
  }))
}

function newTimeRowId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function loadTimeRowsForDate(isoDate: string): StandupTimeRow[] {
  try {
    const raw = localStorage.getItem(`${STANDUP_TIME_STORAGE_PREFIX}:${isoDate}`)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x) => typeof x === 'object' && x !== null && 'id' in x && 'projectId' in x && 'projectLabel' in x)
      .map((x) => {
        const o = x as Record<string, unknown>
        let durationHeures = Number(o.durationHeures)
        if (!Number.isFinite(durationHeures) || durationHeures <= 0) {
          const jours = Number(o.durationJours)
          if (Number.isFinite(jours) && jours > 0) {
            durationHeures = jours * MAX_STANDUP_DAY_HEURES
          }
        }
        return {
          id: String(o.id),
          projectId: Number(o.projectId),
          projectLabel: String(o.projectLabel),
          durationHeures,
        }
      })
      .filter((x) => x.id && Number.isFinite(x.projectId) && x.durationHeures > 0)
  } catch {
    return []
  }
}

function formatDurationBadgeHeures(h: number): string {
  if (Math.abs(h - HEURES_PAR_JOUR_TRAVAIL) < 1e-6) return '1 jour'
  if (Math.abs(h - HEURES_PAR_JOUR_TRAVAIL / 2) < 1e-6) return '0,5 jour'
  return `${h} h`
}

function durationKeyToHeures(key: string): number | null {
  if (key === '1j' || key === '1') return HEURES_PAR_JOUR_TRAVAIL
  if (key === '0.5j' || key === '0.5') return HEURES_PAR_JOUR_TRAVAIL / 2
  return null
}

interface ProjectListItem {
  id: number
  nom: string
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso)
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(t)
}

function MemberEditFormCard({
  draft,
  onDraftChange,
  onSave,
  onCancel,
  saving,
}: {
  draft: MemberDraft
  onDraftChange: (patch: Partial<MemberDraft>) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
}) {
  return (
    <div className="flex w-full flex-row items-center gap-4 rounded-xl border border-zinc-200 bg-zinc-100/30 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <input
          type="text"
          autoComplete="name"
          placeholder="Nom"
          value={draft.nom}
          onChange={(e) => onDraftChange({ nom: e.target.value })}
          className="h-11 w-full rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-medium text-zinc-600 outline-none placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-[#1CB5C8]/35"
        />
        <input
          type="email"
          autoComplete="email"
          placeholder="email@qolaig.com"
          value={draft.email}
          onChange={(e) => onDraftChange({ email: e.target.value })}
          className="h-11 w-full rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-normal text-zinc-600 outline-none placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-[#1CB5C8]/35"
        />
        <input
          type="tel"
          autoComplete="tel"
          placeholder="+33 6 12 34 56 78"
          value={draft.phone}
          onChange={(e) => onDraftChange({ phone: e.target.value })}
          className="h-11 w-full rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm font-normal text-zinc-600 outline-none placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-[#1CB5C8]/35"
        />
      </div>
      <div className="flex w-[96px] shrink-0 items-center justify-start gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className="flex h-9 w-11 shrink-0 items-center justify-center rounded-xl text-[#00A63E] transition-opacity hover:opacity-80 disabled:opacity-50"
          aria-label="Enregistrer"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
          ) : (
            <Check className="h-4 w-4" strokeWidth={2.5} />
          )}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          className="flex h-9 min-w-0 flex-1 items-center justify-center rounded-xl text-[#E7000B] transition-opacity hover:opacity-80 disabled:opacity-50"
          aria-label="Annuler"
        >
          <X className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

function StandupPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isSuperAdmin = user?.role === 'super_admin'
  const isClient = user?.role === 'client'
  const canAccessStandup = !isClient && isQolaigEmail(user?.email)

  const [selectedDate, setSelectedDate] = useState(() => toLocalISODate(new Date()))
  const [board, setBoard] = useState<StandupBoardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [membersOpen, setMembersOpen] = useState(false)
  const [allUsers, setAllUsers] = useState<UserRow[]>([])
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set())
  const [savingMembers, setSavingMembers] = useState(false)
  const [memberEditor, setMemberEditor] = useState<MemberEditorState>(null)
  const [memberDraft, setMemberDraft] = useState<MemberDraft>({ nom: '', email: '', phone: '' })
  const [savingMemberForm, setSavingMemberForm] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editCard, setEditCard] = useState<StandupBoardCard | null>(null)
  const [formContent, setFormContent] = useState('')
  const [savingEntry, setSavingEntry] = useState(false)

  const [projectsForTime, setProjectsForTime] = useState<ProjectListItem[]>([])
  const [loadingProjectsTime, setLoadingProjectsTime] = useState(false)
  const [timeRows, setTimeRows] = useState<StandupTimeRow[]>([])
  const [timeProjectId, setTimeProjectId] = useState('')
  const [timeDurationKey, setTimeDurationKey] = useState('1j')
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false)
  const [timeEntriesSaving, setTimeEntriesSaving] = useState(false)

  const selectedDateObj = useMemo(() => parseISODate(selectedDate), [selectedDate])
  const isToday = toLocalISODate(new Date()) === selectedDate

  const heuresSaisies = useMemo(
    () =>
      timeRows
        .filter((r) => isOwnStandupTimeRow(r, user?.id))
        .reduce((acc, r) => acc + r.durationHeures, 0),
    [timeRows, user?.id]
  )

  const durationSelectOptions = useMemo(() => {
    const rem = Math.max(0, MAX_STANDUP_DAY_HEURES - heuresSaisies)
    const demi = HEURES_PAR_JOUR_TRAVAIL / 2
    const out: { value: string; label: string }[] = []
    if (rem >= HEURES_PAR_JOUR_TRAVAIL) {
      out.push({ value: '1j', label: '1 jour' })
    }
    if (rem >= demi) {
      out.push({ value: '0.5j', label: '0,5 jour' })
    }
    return out
  }, [heuresSaisies])

  const loadBoard = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<StandupBoardResponse>('/standup/board', { date: selectedDate })
      setBoard(res.data)
    } catch (e) {
      console.error(e)
      setBoard(null)
    } finally {
      setLoading(false)
    }
  }, [selectedDate])

  useEffect(() => {
    if (!canAccessStandup) {
      navigate({ to: '/projects' })
      return
    }
    loadBoard()
  }, [canAccessStandup, navigate, loadBoard])

  const loadTimeEntries = useCallback(async () => {
    setTimeEntriesLoading(true)
    setTimeProjectId('')
    try {
      const res = await api.get<StandupTimeEntriesApiResponse>('/standup/time-entries', {
        date: selectedDate,
      })
      const fromApi = rowsFromApiEntries(res.data.entries)
      if (fromApi.length > 0) {
        setTimeRows(fromApi)
        return
      }
      const legacy = loadTimeRowsForDate(selectedDate)
      if (legacy.length > 0) {
        try {
          const putRes = await api.put<StandupTimeEntriesApiResponse>('/standup/time-entries', {
            work_date: selectedDate,
            entries: rowsToApiPayload(legacy),
          })
          setTimeRows(rowsFromApiEntries(putRes.data.entries))
          try {
            localStorage.removeItem(`${STANDUP_TIME_STORAGE_PREFIX}:${selectedDate}`)
          } catch {
            /* ignore */
          }
        } catch {
          setTimeRows(legacy)
        }
        return
      }
      setTimeRows([])
    } catch {
      setTimeRows(loadTimeRowsForDate(selectedDate))
    } finally {
      setTimeEntriesLoading(false)
    }
  }, [selectedDate])

  useEffect(() => {
    if (!canAccessStandup) return
    loadTimeEntries()
  }, [canAccessStandup, loadTimeEntries])

  useEffect(() => {
    if (durationSelectOptions.length === 0) return
    if (!durationSelectOptions.some((o) => o.value === timeDurationKey)) {
      setTimeDurationKey(durationSelectOptions[0].value)
    }
  }, [durationSelectOptions, timeDurationKey])

  useEffect(() => {
    if (!canAccessStandup) return
    let cancelled = false
    ;(async () => {
      setLoadingProjectsTime(true)
      try {
        const res = await api.get<{ data: ProjectListItem[]; count: number; message: string }>(
          '/projects'
        )
        if (!cancelled) setProjectsForTime(res.data?.data ?? [])
      } catch {
        if (!cancelled) setProjectsForTime([])
      } finally {
        if (!cancelled) setLoadingProjectsTime(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [canAccessStandup])

  const resetMemberForm = () => {
    setMemberEditor(null)
    setMemberDraft({ nom: '', email: '', phone: '' })
  }

  const startCreateMember = () => {
    setMemberEditor({ type: 'create' })
    setMemberDraft({ nom: '', email: '', phone: '' })
  }

  const startEditMember = (u: UserRow) => {
    setMemberEditor({ type: 'edit', userId: u.id })
    setMemberDraft({
      nom: u.nom,
      email: u.email,
      phone: (u.phone ?? '').trim(),
    })
  }

  const saveMemberForm = async () => {
    const nom = memberDraft.nom.trim()
    const email = memberDraft.email.trim()
    if (!nom || !email) {
      alert('Le nom et l’e-mail sont requis.')
      return
    }
    setSavingMemberForm(true)
    try {
      if (memberEditor?.type === 'create') {
        const res = await api.post<UserRow>('/users/internal', {
          nom,
          email,
          phone: memberDraft.phone.trim() || undefined,
        })
        const created = res.data
        setAllUsers((prev) =>
          [...prev, { ...created, phone: created.phone ?? null }].sort((a, b) =>
            a.nom.localeCompare(b.nom, 'fr')
          )
        )
        setSelectedMemberIds((prev) => new Set([...prev, created.id]))
        resetMemberForm()
      } else if (memberEditor?.type === 'edit') {
        await api.put(`/users/${memberEditor.userId}`, {
          nom,
          email,
          phone: memberDraft.phone.trim() || null,
        })
        setAllUsers((prev) =>
          prev.map((u) =>
            u.id === memberEditor.userId
              ? {
                  ...u,
                  nom,
                  email,
                  phone: memberDraft.phone.trim() || null,
                }
              : u
          )
        )
        resetMemberForm()
      }
    } catch (e: unknown) {
      console.error(e)
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((x) => (typeof x === 'object' && x && 'msg' in x ? String((x as { msg: string }).msg) : '')).join(' ')
            : 'Enregistrement impossible.'
      alert(msg || 'Enregistrement impossible.')
    } finally {
      setSavingMemberForm(false)
    }
  }

  const openMembersModal = async () => {
    setMembersOpen(true)
    resetMemberForm()
    try {
      const [usersRes, membersRes] = await Promise.all([
        api.get<UserListResponse>('/users', { internal: 'true' }),
        api.get<{ user_ids: string[] }>('/standup/members'),
      ])
      const internal = (usersRes.data?.data ?? []).filter(
        (u) => u.role !== 'client' && isQolaigEmail(u.email)
      )
      setAllUsers(internal)
      setSelectedMemberIds(new Set(membersRes.data?.user_ids ?? []))
    } catch (e) {
      console.error(e)
    }
  }

  const saveMembers = async () => {
    setSavingMembers(true)
    try {
      await api.put('/standup/members', { user_ids: Array.from(selectedMemberIds) })
      setMembersOpen(false)
      await loadBoard()
    } catch (e) {
      console.error(e)
      alert('Impossible d’enregistrer les membres.')
    } finally {
      setSavingMembers(false)
    }
  }

  const toggleMember = (id: string) => {
    setSelectedMemberIds((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const openEdit = (card: StandupBoardCard) => {
    if (user?.id !== card.user.id) return
    setEditCard(card)
    setFormContent(card.entry?.content ?? '')
    setEditOpen(true)
  }

  const saveEntry = async () => {
    if (!editCard) return
    setSavingEntry(true)
    try {
      await api.put<StandupBoardCard>('/standup/entries', {
        standup_date: selectedDate,
        content: formContent,
      })
      setEditOpen(false)
      setEditCard(null)
      await loadBoard()
    } catch (e) {
      console.error(e)
      alert('Impossible d’enregistrer la note.')
    } finally {
      setSavingEntry(false)
    }
  }

  const shiftDay = (delta: number) => {
    const d = parseISODate(selectedDate)
    d.setDate(d.getDate() + delta)
    setSelectedDate(toLocalISODate(d))
  }

  const addTimeRow = async () => {
    if (!user?.id) return
    const heures = durationKeyToHeures(timeDurationKey)
    const maxAjout = Math.max(0, MAX_STANDUP_DAY_HEURES - heuresSaisies)
    if (heures == null || heures > maxAjout) return
    const proj = projectsForTime.find((p) => String(p.id) === timeProjectId)
    if (!proj) {
      alert('Sélectionnez un projet.')
      return
    }
    const next: StandupTimeRow[] = [
      ...timeRows,
      {
        id: newTimeRowId(),
        userId: user.id,
        userNom: user.nom,
        projectId: proj.id,
        projectLabel: proj.nom,
        durationHeures: heures,
      },
    ]
    setTimeEntriesSaving(true)
    try {
      const putRes = await api.put<StandupTimeEntriesApiResponse>('/standup/time-entries', {
        work_date: selectedDate,
        entries: rowsToApiPayload(next.filter((r) => isOwnStandupTimeRow(r, user.id))),
      })
      setTimeRows(rowsFromApiEntries(putRes.data.entries))
      setTimeProjectId('')
    } catch (e: unknown) {
      console.error(e)
      const msg = e instanceof ApiError ? e.message : 'Impossible d’enregistrer la saisie.'
      alert(msg)
    } finally {
      setTimeEntriesSaving(false)
    }
  }

  const removeTimeRow = async (id: string) => {
    const target = timeRows.find((r) => r.id === id)
    if (!target || !isOwnStandupTimeRow(target, user?.id)) return
    const next = timeRows.filter((r) => r.id !== id)
    setTimeEntriesSaving(true)
    try {
      const putRes = await api.put<StandupTimeEntriesApiResponse>('/standup/time-entries', {
        work_date: selectedDate,
        entries: rowsToApiPayload(next.filter((r) => isOwnStandupTimeRow(r, user?.id))),
      })
      setTimeRows(rowsFromApiEntries(putRes.data.entries))
    } catch (e: unknown) {
      console.error(e)
      const msg = e instanceof ApiError ? e.message : 'Impossible d’enregistrer la saisie.'
      alert(msg)
    } finally {
      setTimeEntriesSaving(false)
    }
  }

  const projectSelectOptions = useMemo(
    () => projectsForTime.map((p) => ({ value: String(p.id), label: p.nom })),
    [projectsForTime]
  )

  if (!canAccessStandup) {
    return null
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 flex flex-col gap-4 sm:mb-7 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-[#1a1a1a] sm:text-[1.75rem] sm:leading-tight">
            Standup quotidien
          </h1>
          {isSuperAdmin && (
            <Button
              type="button"
              variant="outline"
              className="h-11 shrink-0 gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-[#1a1a1a] shadow-sm hover:bg-gray-50/90"
              onClick={openMembersModal}
            >
              <Settings className="h-4 w-4 text-[#1CB5C8]" strokeWidth={2} />
              Gérer les membres
            </Button>
          )}
        </div>

        <div className="mb-6 w-full">
          <div
            className={cn(
              'flex w-full min-h-[5.25rem] items-stretch overflow-hidden rounded-2xl',
              'border border-gray-200/90 bg-white',
              'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]'
            )}
          >
            <button
              type="button"
              onClick={() => shiftDay(-1)}
              className="flex w-14 shrink-0 items-center justify-center border-r border-gray-100 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 sm:w-16"
              aria-label="Jour précédent"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2} />
            </button>
            <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-4 py-3 sm:py-4">
              <div className="flex items-center justify-center gap-2.5 text-center">
                <Calendar className="h-4 w-4 shrink-0 text-[#1CB5C8]" strokeWidth={1.75} />
                <span className="text-sm font-semibold leading-snug text-[#1a1a1a] sm:text-base">
                  {formatFrenchDate(selectedDateObj)}
                </span>
              </div>
              {isToday ? (
                <span className="mt-2 inline-flex items-center rounded-full bg-[#e8f8fa] px-3 py-1 text-xs font-semibold text-[#1CB5C8]">
                  Aujourd&apos;hui
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setSelectedDate(toLocalISODate(new Date()))}
                  className="mt-2 inline-flex items-center rounded-full bg-[#e8f8fa] px-3 py-1 text-xs font-semibold text-[#1CB5C8] transition-colors hover:bg-[#d8f3f6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1CB5C8]"
                >
                  Aujourd&apos;hui
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => shiftDay(1)}
              className="flex w-14 shrink-0 items-center justify-center border-l border-gray-100 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 sm:w-16"
              aria-label="Jour suivant"
            >
              <ChevronRight className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>
        </div>

        <section
          className={cn(
            'mb-6 w-full rounded-2xl border border-gray-200/90 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)] sm:p-4',
            timeEntriesLoading && 'pointer-events-none opacity-60'
          )}
          aria-labelledby="standup-time-heading"
        >
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={2} aria-hidden />
            <h2
              id="standup-time-heading"
              className="text-base font-semibold tracking-tight text-[#1a1a1a] sm:text-lg"
            >
              Saisie des temps
            </h2>
          </div>
          <p className="mb-3 text-sm text-gray-600">
            Enregistrez votre temps pour {formatStandupTimeInstruction(selectedDateObj)}.
          </p>

          {timeRows.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-xs font-medium text-gray-500">Temps saisi :</p>
              <ul className="space-y-2">
                {timeRows.map((row) => {
                  const canRemove = isOwnStandupTimeRow(row, user?.id)
                  return (
                    <li
                      key={`${row.userId ?? 'local'}-${row.id}`}
                      className="flex items-center gap-2 rounded-xl border border-gray-100 bg-[#fafbfc] px-3 py-2"
                    >
                      <span className="min-w-[5.5rem] shrink-0 truncate text-xs font-medium text-gray-500 sm:min-w-[7rem]">
                        {row.userNom?.trim() ? row.userNom : '—'}
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium text-[#1a1a1a]">
                        {row.projectLabel}
                      </span>
                      <span className="inline-flex shrink-0 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-gray-600">
                        {formatDurationBadgeHeures(row.durationHeures)}
                      </span>
                      {canRemove ? (
                        <button
                          type="button"
                          onClick={() => void removeTimeRow(row.id)}
                          disabled={timeEntriesSaving || timeEntriesLoading}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          aria-label={`Retirer ${row.projectLabel}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      ) : (
                        <span className="h-8 w-8 shrink-0" aria-hidden />
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="standup-time-project" className="text-xs font-semibold text-gray-600">
                Projet
              </Label>
              <Select
                id="standup-time-project"
                placeholder="Sélectionner"
                value={timeProjectId}
                onChange={(e) => setTimeProjectId(e.target.value)}
                disabled={
                  loadingProjectsTime ||
                  heuresSaisies >= MAX_STANDUP_DAY_HEURES ||
                  timeEntriesLoading ||
                  timeEntriesSaving
                }
                options={projectSelectOptions}
                className="h-9 rounded-xl border-gray-200 bg-white text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="standup-time-duration" className="text-xs font-semibold text-gray-600">
                Durée
              </Label>
              {durationSelectOptions.length > 0 ? (
                <Select
                  id="standup-time-duration"
                  value={timeDurationKey}
                  onChange={(e) => setTimeDurationKey(e.target.value)}
                  disabled={
                    heuresSaisies >= MAX_STANDUP_DAY_HEURES ||
                    timeEntriesLoading ||
                    timeEntriesSaving
                  }
                  options={durationSelectOptions}
                  className="h-9 rounded-xl border-gray-200 bg-white text-sm"
                />
              ) : (
                <div
                  id="standup-time-duration"
                  className="flex h-9 items-center rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 text-sm text-gray-500"
                >
                  Journée complète
                </div>
              )}
            </div>
          </div>
          <Button
            type="button"
            className="mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-[#1CB5C8] text-sm font-semibold text-white shadow-sm hover:bg-[#189eb0] disabled:opacity-50"
            onClick={() => void addTimeRow()}
            disabled={
              !timeProjectId ||
              loadingProjectsTime ||
              durationSelectOptions.length === 0 ||
              heuresSaisies >= MAX_STANDUP_DAY_HEURES ||
              timeEntriesLoading ||
              timeEntriesSaving
            }
          >
            {timeEntriesSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="h-4 w-4" strokeWidth={2.25} />
            )}
            Ajouter
          </Button>
        </section>

        {loading ? (
          <PageSkeleton variant="list" />
        ) : !board?.cards?.length ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white/80 p-10 text-center shadow-sm">
            <p className="text-gray-700 font-medium mb-1">Aucun membre sur le standup</p>
            <p className="text-sm text-gray-500 mb-4">
              Sélectionnez les personnes à suivre pour afficher leurs cartes.
            </p>
            {isSuperAdmin && (
              <Button
                type="button"
                className="bg-[#1CB5C8] text-white hover:bg-[#189eb0]"
                onClick={openMembersModal}
              >
                Gérer les membres
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {board.cards.map((card) => {
              const content = card.entry?.content?.trim() ?? ''
              const isEmpty = !content
              const isOwn = user?.id === card.user.id

              return (
                <article
                  key={card.user.id}
                  role={isOwn ? 'button' : undefined}
                  tabIndex={isOwn ? 0 : undefined}
                  onClick={() => isOwn && openEdit(card)}
                  onKeyDown={(e) => {
                    if (isOwn && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      openEdit(card)
                    }
                  }}
                  className={cn(
                    'flex flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_2px_10px_rgba(15,23,42,0.04)] transition-all duration-200',
                    isOwn && 'cursor-pointer hover:border-gray-300 hover:shadow-[0_3px_14px_rgba(15,23,42,0.07)]'
                  )}
                >
                  <div className="mb-1.5 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold tracking-tight text-[#1a1a1a]">
                        {card.user.nom}
                      </h3>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{card.user.email}</p>
                    </div>
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
                        card.has_completed_update
                          ? 'border-[#1CB5C8] bg-[#1CB5C8] text-white shadow-sm'
                          : 'border-gray-200 bg-white text-gray-300'
                      )}
                      title={card.has_completed_update ? 'Standup renseigné' : 'Pas encore de note'}
                    >
                      {card.has_completed_update ? (
                        <Check className="h-4 w-4 stroke-[3]" />
                      ) : (
                        <Circle className="h-4 w-4" />
                      )}
                    </span>
                  </div>

                  <div
                    className={cn(
                      'min-h-[1.5rem] flex-1',
                      !isEmpty &&
                        'max-h-[120px] overflow-y-auto overflow-x-hidden pr-0.5 [scrollbar-color:rgba(156,163,175,0.5)_transparent] [scrollbar-width:thin]'
                    )}
                  >
                    {isEmpty ? (
                      <p className="px-1 py-1 text-center text-xs italic text-gray-400">
                        Pas encore de note
                      </p>
                    ) : (
                      <StandupTextWithLinks text={content} compact />
                    )}
                  </div>

                  {card.entry?.updated_at && (
                    <p className="mt-1.5 border-t border-gray-100/90 pt-1.5 text-[11px] text-gray-400">
                      Modifié à {formatTime(card.entry.updated_at)}
                    </p>
                  )}
                </article>
              )
            })}
          </div>
        )}
      </main>

      <Dialog
        open={membersOpen}
        onOpenChange={(open) => {
          setMembersOpen(open)
          if (!open) resetMemberForm()
        }}
      >
        <DialogContent className="flex max-h-[85vh] max-w-xl flex-col gap-0 overflow-hidden rounded-2xl border border-gray-200/90 p-0 shadow-xl sm:max-w-xl">
          <DialogHeader className="border-b border-gray-100 px-6 pb-4 pt-6 text-left">
            <DialogTitle className="text-xl font-bold tracking-tight text-[#1a1a1a]">
              Gestion des membres
            </DialogTitle>
            <DialogDescription className="pt-1.5 text-left text-sm text-gray-500">
              Activez ou désactivez les profils affichés sur le tableau standup (équipe @qolaig.com).
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[min(52vh,420px)] space-y-3 overflow-y-auto px-4 py-4">
            {isSuperAdmin && memberEditor?.type === 'create' && (
              <MemberEditFormCard
                draft={memberDraft}
                onDraftChange={(patch) => setMemberDraft((d) => ({ ...d, ...patch }))}
                onSave={saveMemberForm}
                onCancel={resetMemberForm}
                saving={savingMemberForm}
              />
            )}
            {isSuperAdmin && !memberEditor && (
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full rounded-xl border border-gray-200 bg-white text-sm font-medium text-[#1a1a1a] shadow-sm hover:bg-gray-50"
                onClick={startCreateMember}
              >
                <Plus className="mr-2 h-4 w-4 text-[#1CB5C8]" strokeWidth={2} />
                Ajouter un membre
              </Button>
            )}

            {allUsers.map((u) => {
              const active = selectedMemberIds.has(u.id)
              const editing =
                memberEditor?.type === 'edit' && memberEditor.userId === u.id
              if (editing) {
                return (
                  <MemberEditFormCard
                    key={u.id}
                    draft={memberDraft}
                    onDraftChange={(patch) => setMemberDraft((d) => ({ ...d, ...patch }))}
                    onSave={saveMemberForm}
                    onCancel={resetMemberForm}
                    saving={savingMemberForm}
                  />
                )
              }
              return (
                <div
                  key={u.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-200/90 bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1a1a1a]">{u.nom}</p>
                    <p className="truncate text-xs text-gray-500">{u.email}</p>
                    <p className="truncate text-xs text-gray-500">
                      {(u.phone && u.phone.trim()) || '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={cn(
                        'w-10 shrink-0 text-right text-[11px] font-semibold sm:text-xs',
                        active ? 'text-[#1CB5C8]' : 'text-gray-400'
                      )}
                    >
                      {active ? 'Actif' : 'Inactif'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={active}
                      aria-label={active ? `${u.nom} : actif sur le standup` : `${u.nom} : inactif`}
                      onClick={() => toggleMember(u.id)}
                      disabled={memberEditor?.type === 'create'}
                      className={cn(
                        'relative inline-block h-[26px] w-[48px] shrink-0 cursor-pointer rounded-full border-0 p-0 align-middle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1CB5C8]/50 focus-visible:ring-offset-2 active:scale-[0.97]',
                        memberEditor?.type === 'create' && 'opacity-40',
                        active
                          ? 'bg-gradient-to-b from-[#5ecfe0] to-[#1CB5C8] shadow-[inset_0_1px_2px_rgba(255,255,255,0.35),0_1px_2px_rgba(15,23,42,0.08)]'
                          : 'bg-gray-200 shadow-[inset_0_1px_3px_rgba(15,23,42,0.12)] hover:bg-gray-300'
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none absolute top-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(15,23,42,0.18),0_0_0_1px_rgba(15,23,42,0.04)] transition-[left] duration-200 ease-out',
                          active ? 'left-[calc(100%-23px)]' : 'left-[3px]'
                        )}
                      />
                    </button>
                    {isSuperAdmin && (
                      <button
                        type="button"
                        aria-label={`Modifier ${u.nom}`}
                        onClick={() => startEditMember(u)}
                        disabled={!!memberEditor && memberEditor.type === 'create'}
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#1CB5C8]',
                          memberEditor?.type === 'create' && 'pointer-events-none opacity-40'
                        )}
                      >
                        <Pencil className="h-4 w-4" strokeWidth={2} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <DialogFooter className="gap-2 border-t border-gray-100 bg-[#f8f9fa]/80 px-6 py-4 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setMembersOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#1CB5C8] text-white hover:bg-[#189eb0]"
              disabled={savingMembers}
              onClick={saveMembers}
            >
              {savingMembers ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden border border-gray-100 bg-white p-0 shadow-2xl sm:max-w-[460px] sm:rounded-2xl">
          <DialogHeader className="space-y-1.5 border-b border-gray-100 px-6 pb-5 pt-7 pr-14 text-left">
            <DialogTitle className="text-2xl font-bold tracking-tight text-[#1a1a1a]">
              {editCard?.user.nom}
            </DialogTitle>
            <DialogDescription className="text-left text-sm font-normal text-gray-500">
              {formatFrenchDate(selectedDateObj)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-1 flex-col bg-white px-6 py-5">
            <Textarea
              className="min-h-[200px] resize-y rounded-xl border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-800 shadow-inner placeholder:text-gray-400 focus-visible:border-[#1CB5C8]/50 focus-visible:ring-2 focus-visible:ring-[#1CB5C8]/20"
              placeholder="Écrivez votre note quotidienne ici..."
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
            />
          </div>
          <DialogFooter className="flex flex-row flex-wrap items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/50 px-6 py-4 sm:gap-3">
            <p className="min-h-[1rem] text-xs text-gray-400">
              {editCard?.entry?.updated_at
                ? `Sauvegardé à ${formatTime(editCard.entry.updated_at)}`
                : '\u00a0'}
            </p>
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Annuler
              </Button>
              <Button
                type="button"
                className="bg-[#1CB5C8] text-white hover:bg-[#189eb0]"
                disabled={savingEntry}
                onClick={saveEntry}
              >
                {savingEntry ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
